import HelloSign from 'hellosign-embedded';

/**
 * Enhanced HelloSign Embedded client with event logging capabilities.
 *
 * Captures both:
 * - HelloSign SDK client events (open, ready, sign, cancel, etc.)
 * - PostMessage events from embedded webapp (hellosign:* messages)
 *
 * Filters out third-party messages (React DevTools etc.)
 */
class EventCapturingClient {
  constructor() {
    this.client = new HelloSign();
    this.eventLog = [];
    this.eventListeners = [];
    this.isLoggingEnabled = true; // Always enable for now

    this.setupEventCapturing();
  }

  setupEventCapturing() {
    // Capture HelloSign client events (excluding 'message' to avoid duplicates with postMessage)
    const eventsToCapture = [
      'open',
      'ready',
      'sign',
      'finish',
      'cancel',
      'close',
      'error',
      'decline',
      'reassign',
      'send',
      'createTemplate',
    ];

    eventsToCapture.forEach((eventName) => {
      this.client.on(eventName, (data) => {
        const eventData = {
          type: eventName,
          timestamp: new Date().toISOString(),
          data: data || null,
        };

        this.eventLog.push(eventData);

        // Notify any listeners
        this.eventListeners.forEach((listener) => {
          listener(eventData, this.eventLog);
        });
      });
    });

    // Also capture the new signer app messages via window.addEventListener
    window.addEventListener('message', (event) => {
      if (event.data && typeof event.data === 'object' && event.data.type) {
        const messageType = event.data.type;

        // Only capture embedded messages with specific patterns
        const isEmbeddedMessage =
          messageType &&
          // eslint-disable-next-line no-restricted-syntax
          (messageType.startsWith('hellosign:') ||
            messageType === 'initialize' ||
            messageType === 'signature_request_signed' ||
            messageType === 'signature_request_canceled' ||
            messageType === 'signature_request_declined' ||
            messageType === 'signature_request_reassigned' ||
            messageType === 'signature_request_sent' ||
            messageType === 'template_created' ||
            messageType === 'error');

        if (isEmbeddedMessage) {
          // Additional check: make sure it's coming from a valid domain or embedded iframe
          const isFromValidOrigin =
            event.origin &&
            // eslint-disable-next-line no-restricted-syntax
            (event.origin.includes('hellosign.com') ||
              // eslint-disable-next-line no-restricted-syntax
              event.origin.includes('hellofax.com') ||
              // eslint-disable-next-line no-restricted-syntax
              event.origin.includes('dropboxsign.com') ||
              event.origin === window.location.origin || // Same origin
              event.origin.includes('localhost') || // Local development
              event.origin.includes('127.0.0.1') || // Local development
              event.source === window); // Self-posted messages

          // Also check if it has embedded-specific data structure
          const hasEmbeddedStructure =
            event.data &&
            (event.data.event || // V1 structure
              event.data.type || // V2 structure
              event.data.payload); // V2 structure with payload

          if (isFromValidOrigin && hasEmbeddedStructure) {
            const eventData = {
              type: 'postMessage',
              messageType,
              timestamp: new Date().toISOString(),
              data: event.data,
              origin: event.origin,
            };

            this.eventLog.push(eventData);

            // Notify any listeners
            this.eventListeners.forEach((listener) => {
              listener(eventData, this.eventLog);
            });
          }
        }
      }
    });
  }

  // Proxy all client methods
  open(...args) {
    return this.client.open(...args);
  }

  close(...args) {
    return this.client.close(...args);
  }

  on(...args) {
    return this.client.on(...args);
  }

  once(...args) {
    return this.client.once(...args);
  }

  off(...args) {
    return this.client.off(...args);
  }

  // Event logging methods
  addEventListener(listener) {
    this.eventListeners.push(listener);
  }

  removeEventListener(listener) {
    const index = this.eventListeners.indexOf(listener);
    if (index > -1) {
      this.eventListeners.splice(index, 1);
    }
  }

  getEventLog() {
    return [...this.eventLog];
  }

  clearEventLog() {
    this.eventLog = [];
  }

  isEventLoggingEnabled() {
    return this.isLoggingEnabled;
  }
}

const client = new EventCapturingClient();
export default client;
